import React, { useRef, useState, useEffect } from "react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import {
  Package,
  Search,
  Plus,
  Trash2,
  Save,
  Building2,
  Calendar,
  Hash,
  ChevronDown,
  ChevronUp,
  DollarSign,
  ShoppingBag,
} from "lucide-react";

const RestockInventory = () => {
  const searchRef = useRef();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [drugsDropdown, setDrugsDropdown] = useState(false);
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [insertedDrugs, setInsertedDrugs] = useState([]);
  const [totalCost, setTotalCost] = useState(0.0);
  const [quantityRequested, setQuantityRequested] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [recording, setRecording] = useState(false);

  const selectDrug = (drug) => {
    setSelectedDrug(drug);
    setDrugsDropdown(false);
    setSearch("");
    setDrugs([]);
  };

  const getDrugs = async (search_query) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${activeSession?.branch?.id}?page=1&pageSize=10000000000000&search=${search_query}`,
        "GET"
      );
      setDrugs(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setLoading(false);
    }
  };

  const insertDrug = () => {
    if (expiryDate === "") {
      return toast.error("Please specify the expiry date");
    }
    if (selectedDrug) {
      if (quantityRequested) {
        const total_cost =
          parseFloat(selectedDrug?.drug?.selling_price) *
          parseFloat(quantityRequested);
        const newInsertedDrugs = [
          ...insertedDrugs,
          {
            drug: selectedDrug?.drug?.id,
            drug_name: selectedDrug?.drug?.name,
            quantity: quantityRequested,
            batch_number: batchNumber,
            expiry_date: expiryDate,
            unit_price: selectedDrug?.drug?.selling_price,
            total_cost: total_cost.toFixed(2),
          },
        ];
        setSelectedDrug(null);
        setInsertedDrugs(newInsertedDrugs);
        setQuantityRequested("");
        setExpiryDate("");
        setBatchNumber("");
        let total = 0;
        newInsertedDrugs.map((item) => {
          total += parseFloat(item?.total_cost);
        });
        setTotalCost(total.toFixed(2));
      } else {
        toast.error("Please enter the quantity");
      }
    } else {
      toast.error("Select a drug first");
    }
  };

  const removeInsertedDrug = (index) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs.splice(index, 1);
    setInsertedDrugs(newInsertedDrugs);
    let total = 0;
    newInsertedDrugs.map((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };

  const updateInsertedDrug = (index, field, value) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs[index][field] = value;

    if (field === "quantity") {
      const unitPrice = parseFloat(newInsertedDrugs[index]?.unit_price) || 0;
      const parsedQty = parseFloat(value) || 0;
      newInsertedDrugs[index].total_cost = (unitPrice * parsedQty).toFixed(2);
    }

    setInsertedDrugs(newInsertedDrugs);

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost) || 0;
    });
    setTotalCost(total.toFixed(2));
  };

  const recordInvoice = async () => {
    if (insertedDrugs.length <= 0) {
      return toast.error("Insert at least one drug");
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure you want to record this restock?",
      text: "This action will add items to inventory",
      showDenyButton: true,
      confirmButtonText: "Yes, Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setRecording(true);
          const body = JSON.stringify({
            drugs: insertedDrugs,
          });

          await requestWithAuth(
            `/inventory/branch/${activeSession?.branch?.id}/`,
            "POST",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Drugs added successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something went wrong!");
        } finally {
          setRecording(false);
        }
      }
    });
  };

  useEffect(() => {
    if (search.length >= 2) {
      getDrugs(search);
    }
  }, [search]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && !drugsDropdown) {
        insertDrug();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedDrug, quantityRequested, expiryDate, batchNumber, drugsDropdown]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Restock Inventory
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ShoppingBag size={32} />
                Restock Inventory
              </h1>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-gray-600">Branch</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeSession?.branch?.name}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Product Entry */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Add Stock Items
                    </h2>
                    <p className="text-sm text-gray-500">
                      Search and add products to restock
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Branch Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source Branch
                  </label>
                  <div className="relative">
                    <Building2
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      value={activeSession?.branch?.name}
                      disabled
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-700"
                    />
                  </div>
                </div>

                {/* Drug Search */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Product
                  </label>
                  <div
                    className="relative cursor-pointer border border-gray-300 rounded-lg px-4 py-2.5 text-sm flex justify-between items-center"
                    onClick={() => {
                      setDrugsDropdown(!drugsDropdown);
                      searchRef.current?.focus();
                    }}
                  >
                    <span
                      className={
                        selectedDrug?.drug?.name
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {selectedDrug?.drug?.name || "Select a product..."}
                    </span>
                    {drugsDropdown ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>

                  {drugsDropdown && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
                    >
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                          />
                          <input
                            ref={searchRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            type="text"
                            placeholder="Type to search..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {search?.length < 2 ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Please enter at least 2 characters
                          </p>
                        ) : loading ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Searching...
                          </p>
                        ) : drugs?.length > 0 ? (
                          drugs.map((drug_item, idx) => (
                            <div
                              key={idx}
                              onClick={() => selectDrug(drug_item)}
                              className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {drug_item?.drug?.name}
                                </span>
                                <span className="text-sm font-semibold text-blue-600">
                                  GHS {drug_item?.drug?.selling_price}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            No result found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details Grid */}
                {selectedDrug?.drug && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Unit Price
                      </label>
                      <div className="text-sm font-semibold text-gray-900">
                        GHS {selectedDrug?.drug?.selling_price}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={quantityRequested}
                        onChange={(e) => setQuantityRequested(e.target.value)}
                        placeholder="Enter quantity"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar size={14} className="inline mr-1" />
                        Expiry Date
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Hash size={14} className="inline mr-1" />
                        Batch Number (Optional)
                      </label>
                      <input
                        type="text"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                        placeholder="Enter batch number"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={insertDrug}
                  disabled={
                    !selectedDrug?.drug || !quantityRequested || !expiryDate
                  }
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Add to List
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Items List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Package size={20} />
                  Items List ({insertedDrugs.length})
                </h2>
              </div>

              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {insertedDrugs.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {insertedDrugs.map((drug, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {drug?.drug_name}
                            </h3>
                            {drug?.batch_number && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  Batch: {drug?.batch_number}
                                </span>
                              </div>
                            )}
                            <p className="text-xs text-gray-500 mt-0.5">
                              Exp:{" "}
                              {new Date(drug?.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => removeInsertedDrug(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="flex items-center justify-between gap-2 mt-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={drug?.quantity}
                              onChange={(e) =>
                                updateInsertedDrug(
                                  index,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                            />
                            <span className="text-xs text-gray-500">
                              × GHS {drug?.unit_price}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            GHS {drug?.total_cost}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Package size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No items added</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add products to get started
                    </p>
                  </div>
                )}
              </div>

              {insertedDrugs.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">
                        Total Cost
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        GHS {totalCost}
                      </span>
                    </div>

                    {/* Record Button */}
                    <button
                      onClick={recordInvoice}
                      disabled={recording}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      {recording ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Recording...
                        </>
                      ) : (
                        <>
                          <Save size={20} />
                          Apply Posting
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestockInventory;
